import { useCallback } from "react";
import { useParams } from "react-router-dom";
import { MindMapAPI, templateService } from "../Api/api";
import { MindMapContext } from "../Context/contex";

const useMindMapService = () => {
    const { documentId } = useParams();

    const {
        isTemplateDocument,
        nodes,
        edges,
        isExternal,
        setSavingData,
    } = MindMapContext();

    const buildTemplatePayload = useCallback(
        (snapshot = {}) => {
            const resolvedNodes = snapshot.nodes ?? nodes;
            const resolvedEdges = snapshot.edges ?? edges;
            const resolvedIsExternal =
                snapshot.isExternal ?? isExternal ?? false;

            return {
                templateId: documentId,
                data: {
                    milepadData: JSON.stringify({
                        nodes: resolvedNodes,
                        edges: resolvedEdges,
                        isExternal: resolvedIsExternal,
                    }),
                },
                dispatch: setSavingData,
            };
        },
        [nodes, edges, isExternal, documentId, setSavingData]
    );

    const syncTemplate = useCallback(
        (snapshot) => {
            const payload = buildTemplatePayload(snapshot);
            return templateService.updateTemplate(payload);
        },
        [buildTemplatePayload]
    );

    /**
     * Unified executor: run `apiCall()` or, if a template doc,
     * POST the full `snapshot` via `syncTemplate(snapshot)`.
     * Pass `snapshot` to trigger template sync (e.g. `{ nodes, edges }`).
     */
    const execute = useCallback(
        ({ apiCall, snapshot }) => {
            if (isTemplateDocument) {
                return syncTemplate(snapshot);
            }

            return apiCall();
        },
        [isTemplateDocument, syncTemplate]
    );

    const createNode = useCallback(
        (payload, snapshot) =>
            execute({
                apiCall: () => MindMapAPI.createNode(payload),
                snapshot,
            }),
        [execute]
    );

    const createEdge = useCallback(
        (payload, snapshot) =>
            execute({
                apiCall: () => MindMapAPI.createEdge(payload),
                snapshot,
            }),
        [execute]
    );

    const updateNode = useCallback(
        (nodeId, partialPayload, snapshot) =>
            execute({
                apiCall: () => MindMapAPI.updateNode(nodeId, partialPayload),
                snapshot,
            }),
        [execute]
    );

    const updateMany = useCallback(
        (nodesPayload, snapshot) =>
            execute({
                apiCall: () => MindMapAPI.updateMany(nodesPayload),
                snapshot,
            }),
        [execute]
    );

    const deleteNode = useCallback(
        (nodeId, snapshot) =>
            execute({
                apiCall: () => MindMapAPI.deleteNode(nodeId),
                snapshot,
            }),
        [execute]
    );

    const deleteEdge = useCallback(
        (edgeId, snapshot) =>
            execute({
                apiCall: () => MindMapAPI.deleteEdge(edgeId),
                snapshot,
            }),
        [execute]
    );

    const deleteNodeWithEdges = useCallback(
        (nodeId, currentEdges, snapshot) =>
            execute({
                apiCall: async () => {
                    const connectedEdges = currentEdges.filter(
                        (edge) => edge.source === nodeId || edge.target === nodeId
                    );

                    await Promise.all(
                        connectedEdges.map((edge) => MindMapAPI.deleteEdge(edge.id))
                    );

                    return MindMapAPI.deleteNode(nodeId);
                },
                snapshot,
            }),
        [execute]
    );

    const saveImportDataToDb = useCallback(
        (payload, snapshot) =>
            execute({
                apiCall: () => MindMapAPI.saveImportDataToDb(payload),
                snapshot,
            }),
        [execute]
    );

    const updateDocumentPrivacy = useCallback(
        (payload) => MindMapAPI.updateDocumentPrivacy(payload),
        []
    );

    return {
        syncTemplate,
        createNode,
        createEdge,
        updateNode,
        updateMany,
        deleteNode,
        deleteEdge,
        deleteNodeWithEdges,
        saveImportDataToDb,
        updateDocumentPrivacy,
        isTemplateDocument,
    };
};

export default useMindMapService;

